/* eslint-disable */
import React, { useState } from 'react';
import { Chip, Box, Link, Tooltip, IconButton, useTheme } from '@mui/material';
import { OpenInNew, Warning, Error as ErrorIcon, ContentCopy } from '@mui/icons-material';
import type { Ticket } from '../../types/release';
import { ValidationIssues } from './ValidationIssues';

interface TicketBadgeProps {
  ticket: Ticket;
  showValidation?: boolean;
  showLateIndicator?: boolean;
}

export const TicketBadge: React.FC<TicketBadgeProps> = ({
  ticket,
  showValidation = true,
  showLateIndicator = true,
}) => {
  const getStatusColor = (status: string, ticketType?: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    const statusLower = status.toLowerCase();
    const type = ticketType?.toLowerCase() || '';
    
    // Handle deprecated status mappings based on ticket type
    // DEPRECATED statuses are from the current workflow and map to new workflow statuses
    // See: https://outsight.atlassian.net/wiki/spaces/INT/pages/2667086031/Jira+Guidelines+for+Standardized+Ticket+Management+and+Release+Process
    
    // Bug: "QA Acceptance" maps to "In validation" (warning/yellow, NOT done/green)
    if (type === 'bug' && statusLower.includes('qa acceptance')) {
      return 'warning';
    }
    
    // Story/Task: "QA Acceptance" maps to "Done" (success/green)
    // Epic: "QA Acceptance" maps to "In Progress" (primary/blue)
    if (statusLower.includes('qa acceptance')) {
      if (type === 'epic') {
        return 'primary'; // Maps to "In Progress"
      }
      return 'success'; // Maps to "Done" for Story/Task
    }
    
    // Done status (new workflow)
    if (statusLower.includes('done')) {
      return 'success';
    }
    
    // Integrated status (new workflow) and deprecated "Pending Release" (maps to Integrated)
    if (statusLower.includes('integrated') || statusLower.includes('pending release')) {
      return 'info';
    }
    
    // In validation status (new workflow) - for Bugs
    if (statusLower.includes('in validation')) {
      return 'warning';
    }
    
    // In Development (new workflow) and deprecated statuses that map to it
    // Story/Task: "In Progress" and "Software Acceptance" map to "In Development"
    // Bug: "In Progress" maps to "In development"
    if (statusLower.includes('in development') || 
        (type !== 'epic' && statusLower.includes('in progress') && !statusLower.includes('in development')) ||
        (type !== 'epic' && statusLower.includes('software acceptance'))) {
      return 'primary';
    }
    
    // Epic: "In Progress" (new workflow) and deprecated "Software Acceptance"/"QA Acceptance" (map to "In Progress")
    if (type === 'epic') {
      if (statusLower.includes('in progress') || statusLower.includes('software acceptance')) {
        return 'primary';
      }
    }
    
    // Specified (new workflow) and deprecated "Selected For Development" (maps to Specified)
    if (statusLower.includes('specified') || statusLower.includes('selected for development')) {
      return 'default';
    }
    
    // On Hold status
    if (statusLower.includes('on hold')) {
      return 'warning';
    }
    
    return 'default';
  };

  const getTypeColor = (type: string): 'default' | 'primary' | 'secondary' => {
    switch (type) {
      case 'Epic':
        return 'primary';
      case 'Story':
        return 'secondary';
      case 'Task':
      case 'Bug':
        return 'default';
      default:
        return 'default';
    }
  };

  const hasErrors = ticket.validation_issues?.some(issue => issue.severity === 'error');
  const hasWarnings = ticket.validation_issues?.some(issue => issue.severity === 'warning');
  const theme = useTheme();
  const [titleExpanded, setTitleExpanded] = useState(false);
  const titleMaxLength = 100;
  const shouldTruncate = ticket.title.length > titleMaxLength;

  const handleCopyTicketId = async () => {
    try {
      await navigator.clipboard.writeText(ticket.id);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = ticket.id;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: ticket.is_late && showLateIndicator ? 'error.main' : 'divider',
        borderRadius: 1,
        p: 1.25,
        mb: 0.75,
        bgcolor: 'background.paper',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: theme.palette.mode === 'dark'
            ? '0px 2px 8px rgba(0, 0, 0, 0.4)'
            : '0px 2px 8px rgba(0, 0, 0, 0.1)',
        },
        // Theme-aware late ticket styling
        ...(ticket.is_late && showLateIndicator && {
          bgcolor: theme.palette.mode === 'dark'
            ? 'rgba(211, 47, 47, 0.15)' // Darker tint for dark mode
            : 'rgba(211, 47, 47, 0.05)', // Light tint for light mode
          borderLeftWidth: '4px',
          borderLeftColor: 'error.main',
        }),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Link
            href={ticket.link}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              textDecoration: 'none',
              fontWeight: 600,
              color: 'primary.main',
              fontSize: '0.9rem',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            {ticket.id}
          </Link>
          <IconButton
            size="small"
            onClick={handleCopyTicketId}
            sx={{
              p: 0.25,
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
            aria-label="Copy ticket ID"
          >
            <ContentCopy fontSize="small" />
          </IconButton>
          <Link
            href={ticket.link}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                color: 'primary.main',
              },
            }}
            aria-label="Open in Jira"
          >
            <OpenInNew sx={{ fontSize: 16 }} />
          </Link>
        </Box>
        
        <Chip
          label={ticket.type}
          size="small"
          color={getTypeColor(ticket.type)}
          variant="outlined"
          sx={{
            // Ensure good contrast for outlined chips
            borderColor: getTypeColor(ticket.type) === 'primary' ? 'primary.main' :
                        getTypeColor(ticket.type) === 'secondary' ? 'secondary.main' : 
                        ticket.type === 'Bug' ? 'error.main' : 'grey.400',
            color: getTypeColor(ticket.type) === 'primary' ? 'primary.main' :
                   getTypeColor(ticket.type) === 'secondary' ? 'secondary.main' :
                   ticket.type === 'Bug' ? 'error.main' : 'text.primary',
            fontWeight: 500,
          }}
        />
        
        <Chip
          label={ticket.status}
          size="small"
          color={getStatusColor(ticket.status, ticket.type)}
        />
        
        {ticket.is_late && showLateIndicator && (
          <Tooltip title="Ticket is late (not in Done/QA Acceptance/Integrated status after code freeze)">
            <Chip
              icon={<Warning />}
              label="Late"
              size="small"
              color="error"
              variant="outlined"
            />
          </Tooltip>
        )}
        
        {hasErrors && (
          <Tooltip title="Has validation errors">
            <ErrorIcon color="error" fontSize="small" />
          </Tooltip>
        )}
        
        {hasWarnings && !hasErrors && (
          <Tooltip title="Has validation warnings">
            <Warning color="warning" fontSize="small" />
          </Tooltip>
        )}
      </Box>
      
      <Box sx={{ mb: 0.75 }}>
        <Box
          component="span"
          sx={{
            fontWeight: 500,
            fontSize: '0.95rem',
            lineHeight: 1.4,
            cursor: shouldTruncate ? 'pointer' : 'default',
            color: 'text.primary',
          }}
          onClick={() => shouldTruncate && setTitleExpanded(!titleExpanded)}
        >
          {shouldTruncate && !titleExpanded
            ? `${ticket.title.substring(0, titleMaxLength)}...`
            : ticket.title}
        </Box>
      </Box>
      
      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          flexWrap: 'wrap',
          fontSize: '0.8125rem',
          color: 'text.secondary',
          alignItems: 'center',
        }}
      >
        {ticket.assignee && (
          <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
            <strong>Assignee:</strong>&nbsp;{ticket.assignee}
          </Box>
        )}
        {ticket.product_scope && (
          <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
            <strong>Scope:</strong>&nbsp;{ticket.product_scope}
          </Box>
        )}
        {ticket.priority && (
          <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
            <strong>Priority:</strong>&nbsp;{ticket.priority}
          </Box>
        )}
      </Box>
      
      {showValidation && ticket.validation_issues && ticket.validation_issues.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <ValidationIssues issues={ticket.validation_issues} />
        </Box>
      )}
    </Box>
  );
};
